import React, { useEffect, useId, useRef, useState } from "react";
import { Icon } from "./SiteChrome";
import { normalizeLocation } from "../lib/locations";
import "./LocationSearch.css";

export default function LocationSearch({ onSelect, compact = false }) {
  const id = useId();
  const controller = useRef(null);
  const sequence = useRef(0);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  useEffect(
    () => () => {
      sequence.current += 1;
      controller.current?.abort();
    },
    [],
  );
  const reset = (value) => {
    sequence.current += 1;
    controller.current?.abort();
    setQuery(value);
    setResults([]);
    setStatus("idle");
    setError("");
  };
  const search = async (event) => {
    event.preventDefault();
    if (query.trim().length < 2) {
      setError("Enter at least two characters to search.");
      return;
    }
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const requestId = ++sequence.current;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      abort.abort();
    }, 12000);
    setStatus("loading");
    setError("");
    setResults([]);
    try {
      const response = await fetch(
        `/api/locations?q=${encodeURIComponent(query.trim())}`,
        { signal: abort.signal },
      );
      if (!response.ok)
        throw new Error(
          "Search is unavailable. Please try again or use coordinates in the monitor.",
        );
      const data = await response.json();
      if (!Array.isArray(data.results))
        throw new Error(
          "Search returned an unexpected response. Please try again.",
        );
      if (sequence.current !== requestId) return;
      setResults(
        data.results.map(normalizeLocation).filter(Boolean).slice(0, 8),
      );
      setStatus("done");
    } catch (err) {
      if (sequence.current !== requestId) return;
      if (err.name !== "AbortError" || timedOut) {
        setError(
          timedOut ? "Search took too long. Please try again." : err.message,
        );
        setStatus("error");
      }
    } finally {
      clearTimeout(timer);
    }
  };
  return (
    <div
      className={`location-search ${compact ? "location-search-compact" : ""}`}
    >
      <form onSubmit={search} role="search" aria-label="Find a location">
        <label htmlFor={id}>Find a city or place</label>
        <div className="location-search-field">
          <Icon name="pin" size={20} />
          <input
            id={id}
            type="search"
            placeholder="City, country or postal code"
            value={query}
            maxLength={100}
            autoComplete="off"
            aria-describedby={`${id}-status`}
            onChange={(event) => reset(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") reset(query);
            }}
          />
          <button type="submit" disabled={status === "loading"}>
            {status === "loading" ? "Searching…" : "Search"}
            <Icon name="arrow" size={17} />
          </button>
        </div>
      </form>
      <div id={`${id}-status`} className="location-search-status" role="status">
        {status === "loading"
          ? "Finding matching places…"
          : status === "done"
            ? results.length
              ? `${results.length} places found. Choose a result below.`
              : "No places found. Try a nearby city or add the country name."
            : "Search worldwide. For example: Paris, France."}
      </div>
      {error && (
        <p className="location-search-error" role="alert">
          {error}
        </p>
      )}
      {results.length > 0 && (
        <ul className="location-results" aria-label="Matching places">
          {results.map((place, index) => (
            <li key={`${place.lat}:${place.lon}:${index}`}>
              <button
                type="button"
                onClick={() => {
                  reset("");
                  onSelect(place);
                }}
              >
                <span>
                  <strong>{place.name}</strong>
                  <small>
                    {place.country || "Location"} · {place.lat.toFixed(2)},{" "}
                    {place.lon.toFixed(2)}
                  </small>
                </span>
                <Icon name="arrow" size={18} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {status === "done" && (
        <div className="location-credit">
          Search by{" "}
          <a
            href="https://open-meteo.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open-Meteo
          </a>{" "}
          ·{" "}
          <a
            href="https://www.geonames.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            GeoNames
          </a>
        </div>
      )}
    </div>
  );
}
